import { ModalTypes, ModalDefaults, ActionConfigs, Sizes, Types, FieldTypes, Align, Backdrop, ActionSizes, Validators, Colors } from '../../Constants.js';
import { html, escapeHtml, isTrusted, markTrusted } from '../../../ContentComposer/ContentComposer.js';

export function _generateFieldHtml(modal, field) {
    const type = field.type || 'text';

    // Global read-only mode overrides individual readonly flag
    if (modal.settings.readOnly) {
        return modal._generateReadOnlyFieldHtml(field);
    }

    if (!field.id && type !== 'heading') field.id = 'fld_' + Math.floor(Math.random() * 1000000);
    const { id, value = '', readonly, required, placeholder = '', label, options, rows = 3, class: customClass = '', style = '', checked, radioOptions, selectedValue, inline } = field;

    // Checkbox support
            if (type === 'checkbox') {
                const val = field.value !== undefined ? field.value : '1';
                const colorStyle = field.color ? `--gm-field-accent: ${field.color}; accent-color: ${field.color};` : '';
                const labelStyle = field.labelColor ? `color: ${field.labelColor};` : '';
                return html`<div class="gm-check"><input class="gm-check-input" type="checkbox" id="${id}" value="${val}" ${checked ? 'checked' : ''} ${required ? 'required' : ''} style="${colorStyle}"> <label class="gm-check-label" for="${id}" style="${labelStyle}">${label}</label></div>`;
            }

    // Radio group support
            if (type === 'radioGroup') {
                const inlineClass = inline ? 'gm-check-inline' : '';
                const groupColor = field.color;
                const groupLabelColor = field.labelColor;

                const radios = (radioOptions || []).map((opt, i) => {
                    const optLabel = opt.label || opt.value;
                    const optValue = opt.value;
                    const labelClass = opt.labelClass || '';
                    const radioId = `${id}_${i}`;
                    const isChecked = optValue == selectedValue;

                    const color = opt.color || groupColor;
                    const labelColor = opt.labelColor || groupLabelColor;
                    const colorStyle = color ? `--gm-field-accent: ${color}; accent-color: ${color};` : '';
                    const labelStyle = labelColor ? `color: ${labelColor};` : '';

                    return html`<div class="gm-check ${inlineClass}"><input class="gm-check-input" type="radio" name="${id}" id="${radioId}" value="${optValue}" ${isChecked ? 'checked' : ''} ${required ? 'required' : ''} style="${colorStyle}"> <label class="gm-check-label ${labelClass}" for="${radioId}" style="${labelStyle}">${optLabel}</label></div>`;
                });

                const lCol = field.labelCol || modal.settings.labelCol || ModalDefaults.labelCol;
                const iCol = field.inputCol || modal.settings.inputCol || ModalDefaults.inputCol;
                const lAlign = field.labelAlign || modal.settings.labelAlign || ModalDefaults.labelAlign;
                const labelPct = (lCol / 12) * 100;
                const inputPct = (iCol / 12) * 100;
                const alignClassMap = { 'left': 'gm-label-align-left', 'center': 'gm-label-align-center', 'right': 'gm-label-align-right' };
                const lAlignClass = alignClassMap[lAlign] || 'gm-label-align-right';

                const mainLabelStyle = field.labelColor ? `color: ${field.labelColor};` : '';

                return html`<div class="gm-field-row gm-align-center gm-field-container" data-field-id="${id}" style="--gm-label-flex: ${labelPct}%; --gm-input-flex: ${inputPct}%;"><div class="gm-field-label ${lAlignClass}"><label class="gm-label" for="${id}" style="${mainLabelStyle}">${label}: ${required ? html`<span class="gm-text-danger">*</span>` : ''}</label></div><div class="gm-field-input">${radios}</div></div>`;
            }

    if (type === 'hidden') {
        const name = field.name != null && field.name !== '' ? field.name : id;
        return html`<input type="hidden" id="${id}" name="${name}" value="${value}">`;
    }
    
    if (type === 'heading') {
        const color = field.color || null;
        const bg = field.background || field.bg || (color && color.startsWith('#') && !field.textColor ? null : null);
        if (field.variant === 'banner' || field.background || field.bg) {
            const background = field.background || field.bg || color || '#0084ff';
            const textColor = field.textColor || field.color || '#ffffff';
            return html`
            <div class="gm-heading gm-heading-banner" style="background: ${background}; color: ${textColor}; padding: 6px 12px; font-weight: 700; border-radius: 2px; margin-top: 1rem; margin-bottom: 0.75rem;">
                <span style="font-size: 0.9rem; text-transform: uppercase;">${label}</span>
            </div>`;
        }
        const colorStyle = color ? `color: ${color} !important; border-bottom-color: ${color} !important;` : '';
        const colorClass = color ? '' : 'gm-text-primary';
        return html`
        <div class="gm-heading">
            <h5 class="gm-heading-text ${colorClass}" style="${colorStyle}">${label.toUpperCase()}</h5>
        </div>`;
    }

            if (type === 'columns') {
                const cols = field.columns || [];
                const colCount = cols.length;

                // Check if only the first column has a labeled input field (excluding checkboxes/controls), and remaining columns are controls (e.g. checkbox)
                const firstColFields = cols[0]?.fields || [];
                const primaryField = firstColFields.length === 1 && firstColFields[0].label && firstColFields[0].type !== 'checkbox' ? firstColFields[0] : null;
                const remainingColsHaveLabels = cols.slice(1).some(c => (c.fields || []).some(f => f.label && f.type !== 'checkbox'));
                const isSinglePrimaryLabel = Boolean(primaryField && !remainingColsHaveLabels);
                const hasExplicitLabel = Boolean(field.label);

                const colsHtml = cols.map(col => {
                    const width = col.width || Math.floor(12 / colCount);
                    const fieldsHtml = (col.fields || []).map(f => {
                        if (isSinglePrimaryLabel && f === primaryField) {
                            const clone = { ...f, _skipRowLabel: true };
                            return modal._generateFieldHtml(clone);
                        }
                        return modal._generateFieldHtml(f);
                    });
                    const gap = col.rowGap || field.rowGap;
                    const colStyle = gap
                        ? `grid-column: span ${width}; --gm-column-row-gap: ${gap};`
                        : `grid-column: span ${width};`;
                    return html`<div class="gm-col" style="${colStyle}">${fieldsHtml}</div>`;
                });

                const colsGrid = html`<div class="gm-columns">${colsHtml}</div>`;

                if (hasExplicitLabel || isSinglePrimaryLabel) {
                    const rowLabel = field.label || primaryField?.label || '';
                    const fieldId = field.id || primaryField?.id || '';
                    const isRequired = field.required || primaryField?.required || false;
                    const lCol = field.labelCol || modal.settings.labelCol || ModalDefaults.labelCol;
                    const iCol = field.inputCol || modal.settings.inputCol || ModalDefaults.inputCol;
                    const lAlign = field.labelAlign || modal.settings.labelAlign || ModalDefaults.labelAlign;
                    const labelPct = (lCol / 12) * 100;
                    const inputPct = (iCol / 12) * 100;
                    const alignClassMap = { 'left': 'gm-label-align-left', 'center': 'gm-label-align-center', 'right': 'gm-label-align-right' };
                    const lAlignClass = alignClassMap[lAlign] || 'gm-label-align-right';

                    return html`
                        <div class="gm-field-row gm-align-center gm-field-container" data-field-id="${fieldId}" style="--gm-label-flex: ${labelPct}%; --gm-input-flex: ${inputPct}%;">
                            <div class="gm-field-label ${lAlignClass}">
                                <label class="gm-label" for="${fieldId}">
                                    ${rowLabel}: ${isRequired ? html`<span class="gm-text-danger">*</span>` : ''}
                                </label>
                            </div>
                            <div class="gm-field-input">
                                ${colsGrid}
                            </div>
                        </div>`;
                }

                return colsGrid;
            }

    let inputHtml = '';
            let fieldRowClass = 'gm-align-center';
    const commonAttrs = `${readonly ? 'readonly' : ''} ${field.pattern ? `pattern="${field.pattern}"` : ''} ${field.minLength ? `minlength="${field.minLength}"` : ''} ${field.maxLength ? `maxlength="${field.maxLength}"` : ''} ${field.min !== undefined ? `min="${field.min}"` : ''} ${field.max !== undefined ? `max="${field.max}"` : ''}`;

    if (type === 'select') {
        const selectOptions = Array.isArray(options) ? options.map(opt => {
            const optVal = typeof opt === 'object' ? opt.value : opt;
            const optText = typeof opt === 'object' ? opt.text : opt;
            const selected = field.multiple
                ? (Array.isArray(value) ? value.indexOf(optVal) !== -1 : optVal == value) ? 'selected' : ''
                : optVal == value ? 'selected' : '';
            return html`<option value="${optVal}" ${selected}>${optText}</option>`;
        }) : '';
        
        const multipleAttr = field.multiple ? 'multiple' : '';
        const sizeAttr = field.size ? `size="${field.size}"` : '';
        
        inputHtml = html`
            <select id="${id}" class="gm-input ${customClass}" ${multipleAttr} ${sizeAttr} ${readonly ? 'disabled' : ''} style="${style}">
                ${selectOptions}
            </select>`;
    } else if (type === FieldTypes.DATEPICKER) {
        const datepickerClass = `gm-datepicker-field ${customClass}`.trim();
        inputHtml = html`<input type="text" id="${id}" class="gm-input ${datepickerClass}" value="${value}" placeholder="${placeholder}" ${commonAttrs} autocomplete="off" style="${style}">`;
    } else if (type === 'textarea') {
        const placement = (field.labelPlacement || modal.settings.textAreaLabelPlacement || ModalDefaults.textAreaLabelPlacement || 'center').toLowerCase();
        const placementClassMap = {
            top: 'gm-align-start',
            center: 'gm-align-center',
            bottom: 'gm-align-end'
        };
        fieldRowClass = placementClassMap[placement] || 'gm-align-center';
        inputHtml = html`<textarea id="${id}" class="gm-input ${customClass}" rows="${rows}" placeholder="${placeholder}" ${commonAttrs} style="${style}">${value}</textarea>`;
    } else {
        inputHtml = html`<input type="${type}" id="${id}" class="gm-input ${customClass}" value="${value}" placeholder="${placeholder}" ${commonAttrs} autocomplete="off" style="${style}">`;
    }

    // ── Input group (append/prepend) ──
    const hasPrepend = Array.isArray(field.prepend) && field.prepend.length > 0;
    const hasAppend = Array.isArray(field.append) && field.append.length > 0;

    if (hasPrepend || hasAppend) {
        const parts = [];
        const isSeparated = field.inputGroupLayout === 'separated';
        const wrapperClass = isSeparated ? 'gm-input-separated' : 'gm-input-group';

        // Prepend items
        if (hasPrepend) {
            field.prepend.forEach(item => {
                if (item.type === 'tag' || item.type === 'icon') {
                    const iconHtml = item.icon ? html`<i class="bi ${item.icon}"></i>` : '';
                    const textHtml = item.text ? (item.icon ? ` ${item.text}` : item.text) : '';
                    parts.push(html`<span class="gm-input-tag">${iconHtml}${textHtml}</span>`);
                } else if (item.type === 'checkbox') {
                    const colorStyle = item.color ? `--gm-field-accent: ${item.color}; accent-color: ${item.color};` : '';
                    const labelStyle = item.labelColor ? `color: ${item.labelColor};` : '';
                    parts.push(html`<div class="gm-check gm-input-check"><input class="gm-check-input" type="checkbox" id="${item.id}" value="${item.value || '1'}" ${item.checked ? 'checked' : ''} style="${colorStyle}"> <label class="gm-check-label" for="${item.id}" style="${labelStyle}">${item.label || ''}</label></div>`);
                } else {
                    const iconHtml = item.icon ? html`<i class="bi ${item.icon}"></i>` : '';
                    const btnClass = `gm-input-btn${item.btnClass ? ' ' + item.btnClass : ''}`;
                    parts.push(html`<button type="button" class="${btnClass}" data-gm-input-action="${item.key || ''}" title="${item.title || ''}">${iconHtml}${item.text || ''}</button>`);
                }
            });
        }

        // The actual input
        parts.push(inputHtml);

        // Append items
        if (hasAppend) {
            field.append.forEach(item => {
                if (item.type === 'tag' || item.type === 'icon') {
                    const iconHtml = item.icon ? html`<i class="bi ${item.icon}"></i>` : '';
                    const textHtml = item.text ? (item.icon ? ` ${item.text}` : item.text) : '';
                    parts.push(html`<span class="gm-input-tag">${iconHtml}${textHtml}</span>`);
                } else if (item.type === 'checkbox') {
                    const colorStyle = item.color ? `--gm-field-accent: ${item.color}; accent-color: ${item.color};` : '';
                    const labelStyle = item.labelColor ? `color: ${item.labelColor};` : '';
                    parts.push(html`<div class="gm-check gm-input-check"><input class="gm-check-input" type="checkbox" id="${item.id}" value="${item.value || '1'}" ${item.checked ? 'checked' : ''} style="${colorStyle}"> <label class="gm-check-label" for="${item.id}" style="${labelStyle}">${item.label || ''}</label></div>`);
                } else {
                    const iconHtml = item.icon ? html`<i class="bi ${item.icon}"></i>` : '';
                    const btnClass = `gm-input-btn${item.btnClass ? ' ' + item.btnClass : ''}`;
                    parts.push(html`<button type="button" class="${btnClass}" data-gm-input-action="${item.key || ''}" title="${item.title || ''}">${iconHtml}${item.text || ''}</button>`);
                }
            });
        }

        inputHtml = html`<div class="${wrapperClass}">${parts}</div>`;
    }

    if (field._skipRowLabel) {
        return inputHtml;
    }

    const lCol = field.labelCol || modal.settings.labelCol || ModalDefaults.labelCol;
    const iCol = field.inputCol || modal.settings.inputCol || ModalDefaults.inputCol;
    const lAlign = field.labelAlign || modal.settings.labelAlign || ModalDefaults.labelAlign;
    
    const alignClassMap = {
        'left': 'gm-label-align-left',
        'center': 'gm-label-align-center',
        'right': 'gm-label-align-right'
    };
    const lAlignClass = alignClassMap[lAlign] || 'gm-label-align-right';

    const labelPct = (lCol / 12) * 100;
    const inputPct = (iCol / 12) * 100;

    return html`
        <div class="gm-field-row ${fieldRowClass} gm-field-container" data-field-id="${id}" style="--gm-label-flex: ${labelPct}%; --gm-input-flex: ${inputPct}%;">
            <div class="gm-field-label ${lAlignClass}">
                <label class="gm-label" for="${id}">
                    ${label}: ${required ? html`<span class="gm-text-danger">*</span>` : '' }
                </label>
            </div>
            <div class="gm-field-input">
                ${inputHtml}
                <div class="gm-error-msg"></div>
            </div>
        </div>`;
}

export function _generateReadOnlyFieldHtml(modal, field) {
    const type = field.type || 'text';
    const { id, value = '', label, options, checked, radioOptions, selectedValue } = field;

    // Hidden fields still render as hidden inputs (needed for data collection)
    if (type === 'hidden') {
        const name = field.name != null && field.name !== '' ? field.name : id;
        return html`<input type="hidden" id="${id}" name="${name}" value="${value}">`;
    }

    // Headings unchanged — skip the readOnly check to avoid recursion
    if (type === 'heading') {
        const color = field.color || null;
        if (field.variant === 'banner' || field.background || field.bg) {
            const background = field.background || field.bg || color || '#0084ff';
            const textColor = field.textColor || field.color || '#ffffff';
            return html`
            <div class="gm-heading gm-heading-banner" style="background: ${background}; color: ${textColor}; padding: 6px 12px; font-weight: 700; border-radius: 2px; margin-top: 1rem; margin-bottom: 0.75rem;">
                <span style="font-size: 0.9rem; text-transform: uppercase;">${field.label}</span>
            </div>`;
        }
        const colorStyle = color ? `color: ${color} !important; border-bottom-color: ${color} !important;` : '';
        const colorClass = color ? '' : 'gm-text-primary';
        return html`
        <div class="gm-heading">
            <h5 class="gm-heading-text ${colorClass}" style="${colorStyle}">${field.label.toUpperCase()}</h5>
        </div>`;
    }

    // Columns — recursively render children as read-only
    if (type === 'columns') {
        const colCount = (field.columns || []).length;
        const colsHtml = (field.columns || []).map(col => {
            const width = col.width || Math.floor(12 / colCount);
            const fields = (col.fields || []).map(f => modal._generateReadOnlyFieldHtml(f));
            const gap = col.rowGap || field.rowGap;
            const colStyle = gap
                ? `grid-column: span ${width}; --gm-column-row-gap: ${gap};`
                : `grid-column: span ${width};`;
            return html`<div class="gm-col" style="${colStyle}">${fields}</div>`;
        });
        return html`<div class="gm-columns">${colsHtml}</div>`;
    }

    // Checkbox
    if (type === 'checkbox') {
        const colorStyle = field.color ? `color: ${field.color};` : '';
        const labelStyle = field.labelColor ? `color: ${field.labelColor};` : '';
        const useDisabledStyle = modal.settings.readOnly && (modal.settings.readOnlyStyle === 'disabled' || modal.settings.readOnlyStyle === 'input');
        if (useDisabledStyle) {
            return html`<div class="gm-check gm-readonly-field"><input class="gm-check-input gm-readonly-input" type="checkbox" disabled ${checked ? 'checked' : ''}> <label class="gm-check-label" style="${labelStyle}">${label}</label></div>`;
        }
        const icon = checked ? html`<i class="bi bi-check-square-fill" style="font-size:1.1rem;${colorStyle}"></i>` : html`<i class="bi bi-square" style="font-size:1.1rem;color:#ccc"></i>`;
        return html`<div class="gm-check gm-readonly-field"><span class="gm-readonly-control">${icon}</span> <span class="gm-check-label" style="${labelStyle}">${label}</span></div>`;
    }

    // Radio group
    if (type === 'radioGroup') {
        const selected = (radioOptions || []).find(o => o.value == selectedValue);
        const displayVal = selected ? (selected.label || selected.value) : '—';
        const color = selected?.color || field.color;
        const colorStyle = color ? `color: ${color};` : '';
        const lCol = field.labelCol || modal.settings.labelCol || ModalDefaults.labelCol;
        const iCol = field.inputCol || modal.settings.inputCol || ModalDefaults.inputCol;
        const lAlign = field.labelAlign || modal.settings.labelAlign || ModalDefaults.labelAlign;
        const labelPct = (lCol / 12) * 100;
        const inputPct = (iCol / 12) * 100;
        const alignClassMap = { left: 'gm-label-align-left', center: 'gm-label-align-center', right: 'gm-label-align-right' };
        const lAlignClass = alignClassMap[lAlign] || 'gm-label-align-right';
        const useDisabledStyle = modal.settings.readOnly && (modal.settings.readOnlyStyle === 'disabled' || modal.settings.readOnlyStyle === 'input');
        const valueControl = useDisabledStyle
            ? html`<input type="text" class="gm-input gm-readonly-input" readonly disabled value="${displayVal}" style="${colorStyle}">`
            : html`<span class="gm-readonly-value" style="${colorStyle}">${displayVal}</span>`;
        return html`
            <div class="gm-field-row gm-readonly-row gm-align-center gm-field-container" data-field-id="${id}" style="--gm-label-flex: ${labelPct}%; --gm-input-flex: ${inputPct}%;">
                <div class="gm-field-label ${lAlignClass}">
                    <label class="gm-label">${label}:</label>
                </div>
                <div class="gm-field-input">
                    ${valueControl}
                </div>
            </div>`;
    }

    // Select
    if (type === 'select') {
        if (field.multiple) {
            const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
            const displayVal = selectedValues.length > 0 ? selectedValues.join(', ') : '—';
            return modal._renderReadonlyRow(id, label, displayVal, field);
        }
        const selected = Array.isArray(options) ? options.find(o => {
            const optVal = typeof o === 'object' ? o.value : o;
            return optVal == value;
        }) : null;
        const displayVal = selected ? (typeof selected === 'object' ? selected.text : selected) : (value || '—');
        return modal._renderReadonlyRow(id, label, displayVal, field);
    }

    // All text-like inputs
    return modal._renderReadonlyRow(id, label, value || '—', field);
}

export function _renderReadonlyRow(modal, id, label, displayValue, field = {}) {
    const isStacked = field.layout === 'stacked' || field.labelPlacement === 'top' || modal.settings.layout === 'stacked';
    const lCol = field.labelCol || modal.settings.labelCol || ModalDefaults.labelCol;
    const iCol = field.inputCol || modal.settings.inputCol || ModalDefaults.inputCol;
    const lAlign = field.labelAlign || modal.settings.labelAlign || ModalDefaults.labelAlign;
    const labelPct = (lCol / 12) * 100;
    const inputPct = (iCol / 12) * 100;
    const alignClassMap = { left: 'gm-label-align-left', center: 'gm-label-align-center', right: 'gm-label-align-right' };
    const lAlignClass = alignClassMap[lAlign] || 'gm-label-align-right';
    const stackedClass = isStacked ? 'gm-stacked' : '';
    const labelText = label ? (label.endsWith(':') ? label : `${label}:`) : '';
    const valueStyle = field.color || field.valueColor ? `color: ${field.color || field.valueColor};` : '';

    const valContent = typeof displayValue === 'string' && displayValue.includes('<') ? markTrusted(displayValue) : displayValue;
    const useDisabledStyle = modal.settings.readOnly && (modal.settings.readOnlyStyle === 'disabled' || modal.settings.readOnlyStyle === 'input');

    let inputHtml;
    if (useDisabledStyle) {
        if (field.type === 'textarea') {
            inputHtml = html`<textarea class="gm-input gm-readonly-input" readonly disabled rows="${field.rows || 3}" style="${valueStyle}">${valContent}</textarea>`;
        } else {
            inputHtml = html`<input type="text" class="gm-input gm-readonly-input" readonly disabled value="${valContent}" style="${valueStyle}">`;
        }
    } else {
        inputHtml = html`<span class="gm-readonly-value" style="${valueStyle}">${valContent}</span>`;
    }

    return html`
        <div class="gm-field-row gm-readonly-row ${stackedClass} gm-align-center gm-field-container" data-field-id="${id}" style="--gm-label-flex: ${labelPct}%; --gm-input-flex: ${inputPct}%;">
            <div class="gm-field-label ${lAlignClass}">
                <label class="gm-label">${labelText}</label>
            </div>
            <div class="gm-field-input">
                ${inputHtml}
            </div>
        </div>`;
}

export function _getAllFields(modal, fields = null) {
    if (!fields) fields = modal.settings.fields || [];
    let all = [];
    fields.forEach(f => {
        all.push(f);
        if (f.type === 'columns' && Array.isArray(f.columns)) {
            f.columns.forEach(col => {
                if (Array.isArray(col.fields)) {
                    all = all.concat(modal._getAllFields(col.fields));
                }
            });
        }
    });
    return all;
}

